import {
  CommandBar,
  DetailsListLayoutMode,
  IColumn,
  ICommandBarItemProps,
  IconButton,
  Link,
  mergeStyles,
  Modal,
  SearchBox,
  SelectionMode,
  Spinner,
  Stack,
  Text,
} from '@fluentui/react';
import {
  DetailsList,
  IDetailsColumnProps,
  Selection,
} from '@fluentui/react/lib/DetailsList';
import { useBoolean } from '@fluentui/react-hooks';
import { formatDate, resolveOtherInfoObj } from '@moko/utils';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoles } from '../../authConfig';
import {
  scrollStackItemStyles,
  stackStyle,
} from '../../common/styles/StackStyles';
import { IPagedCollection } from '../../services/assetServices';
import { getMetadata, IField } from '../../services/metadataService';
import {
  analyseIssueImage,
  editIssue,
  getIssueImage,
  getIssues,
  IIssue,
  IIssueImage,
} from '../../services/issueService';
import { createWork, IWorkResponse } from '../../services/workService';
import AppContext from '../AppContext';
import HeaderWithFilter from '../../common/HeaderWithFilter';
import AssetSelectorPanel from './AssetSelectorPanel';
import {
  errorMessageAtom,
  isInProgressAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { profileDataAtom } from '../../atoms/authAtoms';
import { useAtomValue, useSetAtom } from 'jotai';
import LoadingSign from '../../common/LoadingSign';
import {
  gridStyles,
  focusZoneProps,
  gridStylesNames,
} from '../../common/styles/GridStyles';

function Issues() {
  const [issues, setIssues] = useState<IIssue[]>();
  const [isLastPage, setIsLastPage] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [page, setPage] = useState({ no: 1, search: '', filters: '' });
  const [shimmered, setShimmered] = useState(false);
  const [currentIssue, setCurrentIssue] = useState(0);
  const [selectedIssues, setSelectedIssues] = useState<IIssue[]>([]);
  const [refreshCount, setRefreshCount] = useState(0);
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const [isGettingAnalysis, setIsGettingAnalysis] = useState(false);

  const navigate = useNavigate();

  const context = useContext(AppContext);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const profileData = useAtomValue(profileDataAtom);

  const _columns: IColumn[] = [
    {
      key: 'Id',
      name: 'Id',
      fieldName: 'id',
      minWidth: 25,
      maxWidth: 50,
      isResizable: true,
    },
    {
      key: 'uuid',
      name: 'UUID',
      fieldName: 'uuid',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'Comment',
      name: 'Comment',
      fieldName: 'comment',
      minWidth: 250,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'image',
      name: 'Image',
      fieldName: 'imageUrl',
      minWidth: 75,
      maxWidth: 75,
    },
    {
      key: 'assetId',
      name: 'Asset Id',
      fieldName: 'assetId',
      minWidth: 50,
      maxWidth: 75,
      isResizable: true,
      onRender: (item) => (
        <Link
          key={item.assetId}
          onClick={() => navigate(`/editAsset?id=${item.assetId}`)}
        >
          {item.assetId}
        </Link>
      ),
    },
    {
      key: 'createdAt',
      name: 'CreateAt',
      onRenderHeader: (props?: IDetailsColumnProps) => {
        return (
          <HeaderWithFilter
            columnName={props?.column.name ?? ''}
            columnDbName="CREATED_AT"
            type="Date"
            onFilterSelected={(filterStr: string) =>
              setPage({ ...page, filters: filterStr })
            }
            onFilterCleared={() => setPage({ ...page, filters: '' })}
          />
        );
      },
      fieldName: 'createdAt',
      minWidth: 125,
      maxWidth: 155,
      isResizable: true,
    },
    {
      key: 'issueTypeName',
      name: 'Issue Type',
      fieldName: 'issueTypeName',
      minWidth: 125,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: 'crmCaseId',
      name: 'CRM Case Id',
      fieldName: 'crmCaseId',
      minWidth: 75,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'crmCaseStatusId',
      name: 'CRM Status',
      fieldName: 'crmCaseStatusId',
      minWidth: 75,
      maxWidth: 100,
      isResizable: true,
    },
  ];
  const columns = useRef<IColumn[]>(_columns);

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async () => {
      context.setSelectedTab('Issues');
      setIsInProgress(true);

      try {
        const metadata = await getMetadata(abortController, 'Issue');
        const otherInfoFields = metadata?.fields.filter((mf) => mf.showInGrid);

        const data: IPagedCollection<IIssue> = await getIssues(
          abortController,
          10,
          page.no,
          page.search,
          page.filters
        );
        const _issues = data?.items?.map((issue) => {
          const newIssue: any = {
            ...issue,
            createdAt: formatDate(issue.createdAt, true),
          };

          const oi = resolveOtherInfoObj(issue.otherInfo);
          if (oi) {
            otherInfoFields?.forEach((mf: IField) => {
              newIssue[mf.name] = oi[mf.name];
            });
          }
          otherInfoFields?.forEach((mf) => {
            if (!columns.current?.find((column) => column.name === mf.name)) {
              columns.current?.push({
                key: mf.name,
                name: mf.name,
                fieldName: mf.name,
                minWidth: 100,
                onRender:
                  mf.fieldType === 'Date'
                    ? (item: any) => (
                        <span>
                          {item[mf.name]
                            ? formatDate(String(item[mf.name]))
                            : ''}
                        </span>
                      )
                    : mf.fieldType === 'AutoComplete'
                    ? (item: any) => (
                        <span>{(item[mf.name] ?? ':').split(':')[1]}</span>
                      )
                    : undefined,
              });
            }
          });

          return newIssue;
        });

        setIssues(_issues);

        setIsLastPage(data?.isLastPage);
        setPageCount(data?.pageCount);
        setShimmered(false);
      } catch (error: any) {
        console.error('Error:', error);
        setErrorMessage(error.message);
      } finally {
        setIsInProgress(false);
      }
    };

    fetchData();
    return () => {
      abortController.abort();
    };
  }, [page.no, page.search, page.filters, refreshCount]);

  const itemClass = mergeStyles({
    selectors: {
      '&:hover': {
        textDecoration: 'underline',
        cursor: 'pointer',
      },
    },
  });
  const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] =
    useBoolean(false);
  const [
    isAssetSelectorOpen,
    { setTrue: showAssetSelector, setFalse: hideAssetSelector },
  ] = useBoolean(false);

  const [imageIndex, setImageIndex] = useState(0);
  const handleClickOnImageLink = (issue: IIssue | undefined) => {
    if (!issue || !issues) {
      return;
    }
    if (issue.images?.length > 0) {
      showModal();
      return;
    }
    const imagesUrlParts = issue.imageUrl.split(',');
    issue.images = new Array(imagesUrlParts.length);
    issue.imagesCaption = new Array(imagesUrlParts.length);
    const abortController = new AbortController();
    const baseUrl =
      imagesUrlParts[0].substring(0, imagesUrlParts[0].lastIndexOf('/')) + '/';
    for (let index = 1; index < imagesUrlParts.length; index++) {
      imagesUrlParts[index] = baseUrl + imagesUrlParts[index];
    }
    issue.imagesUrls = imagesUrlParts;
    issue.imagesCaption = imagesUrlParts.map((url: string) => '');
    for (let imgIndex = 0; imgIndex < imagesUrlParts.length; imgIndex++) {
      getIssueImage(abortController, imagesUrlParts[imgIndex])
        .then((data: IIssueImage) => {
          const index = issues.indexOf(issue);
          issues[index].images[imgIndex] = data.imageContent;
          issues[index].imagesCaption[imgIndex] = data.caption;
          setIssues(issues);
          setCurrentIssue(index);
          showModal();
        })
        .catch((error) => {
          console.error('Error:', error);
          setErrorMessage(error.message);
        });
    }
    if (imagesUrlParts.length > 0) {
      setImageIndex(0);
    }
  };

  const handleCreateWork = async () => {
    const abortController = new AbortController();
    setIsInProgress(true);
    const issue = selectedIssues[0];
    createWork(
      abortController,
      {
        assetId: issue.assetId,
        zoneId: issue.zoneId,
        description:
          `Generated by FMS [Comment: ${issue.comment}] ` +
          `[I${issue.id}${issue.zoneId ? `-Z${issue.zoneId}` : ''}` +
          `${issue.zoneAssetCat ? `-AC${issue.zoneAssetCat}` : ''}` +
          `${issue.issueTypeId ? `-IT${issue.issueTypeId}` : ''}]`,
        zoneAssetCatId: selectedIssues[0].zoneAssetCat,
        issueTypeId: selectedIssues[0].issueTypeId,
      },
      issue.id
    )
      .then((workResponse: IWorkResponse) => {
        if (workResponse?.workId) {
          setSuccessMessage(
            `Work with id: ${workResponse?.workId} successfully created.`
          );
        } else {
          setErrorMessage('Work creation failed.');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        setErrorMessage(error.message);
      })
      .finally(() => setIsInProgress(false));
  };

  const _renderItemColumn = (
    item: IIssue,
    index?: number,
    column?: IColumn
  ) => {
    const fieldContent = item[column?.fieldName as keyof IIssue] as string;

    switch (column?.key) {
      case 'image':
        return item.imageUrl ? (
          <Link
            className={itemClass}
            disabled={item?.imageUrl === ''}
            onClick={() => handleClickOnImageLink(item)}
          >
            {(item.imageUrl.match(/,/g) || []).length > 0
              ? `images (${item.imageUrl.split(',').length})`
              : 'image'}
          </Link>
        ) : (
          <span></span>
        );
      default:
        return <span>{fieldContent}</span>;
    }
  };

  const _items: ICommandBarItemProps[] = [
    {
      key: 'refresh',
      text: 'Refresh',
      iconProps: { iconName: 'Refresh' },
      onClick: () => {
        setRefreshCount(refreshCount + 1);
      },
    },
    {
      key: 'newIssue',
      text: 'New',
      iconProps: { iconName: 'Add' },
      disabled: !profileData.roles.includes(appRoles.Admin),
      subMenuProps: {
        items: [
          {
            key: 'newIssueByZone',
            text: 'By Zone',
            iconProps: { iconName: 'EngineeringGroup' },
            onClick: () => navigate('/newIssue/zone'),
          },
          {
            key: 'newIssueByAsset',
            text: 'By Asset',
            onClick: () => navigate('/newIssue/asset'),
          },
        ],
      },
    },
    {
      key: 'editIssue',
      text: 'Edit',
      iconProps: { iconName: 'Edit' },
      onClick: () => navigate(`/editIssue?id=${selectedIssues[0].id}`),
      disabled:
        !profileData.roles.includes(appRoles.Admin) ||
        selectedIssues.length === 0,
    },
    {
      key: 'createWork',
      text: 'Create Work',
      iconProps: { iconName: 'WorkItem' },
      onClick: () => handleCreateWork(),
      disabled:
        !profileData.roles.includes(appRoles.Admin) ||
        selectedIssues.length === 0,
    },
    {
      key: 'setIssueAsset',
      text: 'Set Asset',
      iconProps: { iconName: 'Link' },
      onClick: () => {
        showAssetSelector();
      },
      disabled:
        !profileData.roles.includes(appRoles.Admin) ||
        selectedIssues.length === 0,
    },
    {
      key: 'deleteIssue',
      text: 'Delete',
      iconProps: { iconName: 'Delete' },
      disabled: true,
      // onClick: () => {
      //   context.setSelectedItem({ id: 0, uuid: "" });
      //   navigate(`/assetDetail?action=delete&itemId=${context.selectedItem.id}`);
      // },
      // disabled: !profileData.roles.includes(appRoles.Admin) || context.selectedItem.uuid === "",
    },
  ];
  const _selection = new Selection({
    onSelectionChanged: () => {
      setSelectedIssues(
        _selection.getSelection()?.map((item) => item as IIssue)
      );
    },
  });
  return (
    <>
      <Stack>
        <Stack horizontal verticalAlign="center">
          <CommandBar
            items={_items}
            ariaLabel="Items actions"
            primaryGroupAriaLabel="Items actions"
            farItemsGroupAriaLabel="More actions"
          />
          <SearchBox
            placeholder="Search issue"
            onSearch={(newValue) =>
              setPage({ ...page, no: 1, search: newValue || '' })
            }
            onClear={() => setPage({ ...page, no: 1, search: '' })}
          />
        </Stack>
        <Stack styles={stackStyle}>
          <Stack.Item align="start" styles={scrollStackItemStyles}>
            {issues !== undefined ? (
              <DetailsList
                items={issues}
                columns={columns.current}
                setKey="set"
                layoutMode={DetailsListLayoutMode.fixedColumns}
                selection={_selection}
                onRenderItemColumn={_renderItemColumn}
                selectionMode={SelectionMode.single}
                selectionPreservedOnEmptyClick={true}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="select row"
                styles={gridStyles}
                focusZoneProps={focusZoneProps}
                selectionZoneProps={{
                  className: gridStylesNames.selectionZone,
                }}
              />
            ) : (
              <LoadingSign message="Loading..." iconName="IssueSolid" />
            )}
          </Stack.Item>
        </Stack>
        <Stack horizontal horizontalAlign="space-between">
          <Stack.Item grow={1} align="center">
            <IconButton
              iconProps={{ iconName: 'DoubleChevronLeft' }}
              disabled={page.no === 1}
              onClick={() => setPage({ ...page, no: 1 })}
            />
            <IconButton
              iconProps={{ iconName: 'ChevronLeft' }}
              disabled={page.no === 1}
              onClick={() => setPage({ ...page, no: page.no - 1 })}
            />
            <Text>
              {page.no} of {pageCount}
            </Text>
            <IconButton
              iconProps={{ iconName: 'ChevronRight' }}
              disabled={isLastPage}
              onClick={() => setPage({ ...page, no: page.no + 1 })}
            />
            <IconButton
              iconProps={{ iconName: 'DoubleChevronRight' }}
              disabled={isLastPage}
              onClick={() => setPage({ ...page, no: pageCount })}
            />
          </Stack.Item>
        </Stack>
      </Stack>
      <Modal isOpen={isModalOpen} onDismiss={hideModal} isBlocking={false}>
        {issues &&
        issues[currentIssue]?.images !== undefined &&
        issues[currentIssue]?.images.length > 0 ? (
          <Stack>
            <img src={issues[currentIssue]?.images[imageIndex]} alt="" />
            <Stack horizontal>
              <IconButton
                iconProps={{ iconName: 'ChevronLeft' }}
                className="CaptureButtonStyle"
                onClick={() => {
                  setImageIndex(imageIndex > 0 ? imageIndex - 1 : 0);
                }}
                disabled={imageIndex === 0}
              />
              {imageIndex}
              <IconButton
                iconProps={{ iconName: 'ChevronRight' }}
                className="CaptureButtonStyle"
                onClick={() => {
                  setImageIndex(
                    imageIndex < issues[currentIssue]?.images.length
                      ? imageIndex + 1
                      : issues[currentIssue]?.images.length
                  );
                }}
                disabled={
                  imageIndex === issues[currentIssue]?.images.length - 1
                }
              />
              {isGettingAnalysis ? (
                <Spinner />
              ) : (
                <IconButton
                  iconProps={{ iconName: 'RedEye' }}
                  disabled={!!issues[currentIssue].imagesCaption[imageIndex]}
                  onClick={async () => {
                    try {
                      setIsGettingAnalysis(true);
                      const abortController = new AbortController();
                      const analysisResult = await analyseIssueImage(
                        abortController,
                        currentIssue,
                        issues[currentIssue]?.imagesUrls[imageIndex]
                      );
                      issues[currentIssue].imagesCaption[imageIndex] =
                        analysisResult.caption;
                      setIssues(issues.map((issue) => issue));
                      console.log(analysisResult.caption);
                    } catch (error: any) {
                      console.log(error);
                    } finally {
                      setIsGettingAnalysis(false);
                    }
                  }}
                />
              )}
              <Text variant="small">
                {issues[currentIssue].imagesCaption[imageIndex]}
              </Text>
            </Stack>
          </Stack>
        ) : (
          <></>
        )}
      </Modal>
      <AssetSelectorPanel
        isOpen={isAssetSelectorOpen}
        hideAssetSelector={hideAssetSelector}
        onAssetLinkClick={(assetId) => {
          const abortController = new AbortController();
          const updatedIssue = { assetId: assetId };
          editIssue(abortController, selectedIssues[0].id, updatedIssue).then(
            () => {
              hideAssetSelector();
              setRefreshCount(refreshCount + 1);
            }
          );
        }}
      />
    </>
  );
}

export default Issues;
